/**
 * RawFloorPlan (Apple RoomPlan output, meters, arbitrary origin) →
 * FloorPlanRoom (primary-rectangle model, feet, room-local coordinates).
 *
 * Pipeline: grid-align the whole raw plan → normalize walls → chain the
 * perimeter loop → choose the primary rectangle → build the room skeleton
 * (alcoves / chamfers / free walls) → attach doors, windows & openings →
 * convert objects. Anything that can't be represented faithfully lands in
 * the ConversionReport instead of vanishing.
 */

import { FloorPlanRoom, RawFloorPlan } from '../schemas';
import { normalizeFloorPlan } from './align';
import { attachOpenings } from './attachOpenings';
import { buildRoomShape, makeToRoom } from './buildRoomShape';
import {
  ConversionOptions,
  ConversionReport,
  DEFAULT_CONVERSION_OPTIONS,
  emptyReport,
  FT_PER_M,
} from './conversionTypes';
import { convertObjects } from './convertObjects';
import { normalizeWalls } from './normalizeWalls';
import { buildPerimeterLoop } from './perimeterLoop';
import { choosePrimaryRect } from './primaryRect';

export interface ConversionResult {
  room: FloorPlanRoom;
  report: ConversionReport;
}

export function convertRawFloorPlan(
  rawInput: RawFloorPlan,
  options: Partial<ConversionOptions> = {},
): ConversionResult {
  const opts: ConversionOptions = { ...DEFAULT_CONVERSION_OPTIONS, ...options };
  const report = emptyReport();

  // Grid-align FIRST, on the whole plan: doors/windows/openings/objects are
  // geometrically tied to the walls, so all of it has to rotate together, or
  // it goes physically inconsistent the moment the walls move. Every stage
  // after this sees a plan whose walls are maximally on-grid.
  const raw = normalizeFloorPlan(rawInput, {
    minWallRatio: opts.gridAlignMinWallRatio,
    inlierToleranceDeg: opts.gridAlignInlierToleranceDeg,
  });

  const cleanWalls = normalizeWalls(raw.walls, opts, report);
  const { ring: loop, walls } = buildPerimeterLoop(cleanWalls, opts, report);
  const choice = choosePrimaryRect(loop, walls);

  const heightM = Math.max(0, ...raw.walls.map(w => w.baseHeight + w.height));
  const shape = buildRoomShape(choice, heightM > 0 ? heightM * FT_PER_M : undefined, report);

  const toRoom = makeToRoom(choice.rect);
  const room = attachOpenings(
    shape,
    [
      { kind: 'door', list: raw.doors },
      { kind: 'window', list: raw.windows },
      { kind: 'opening', list: raw.openings },
    ],
    toRoom,
    opts,
    report,
  );

  // Room coords keep the (already grid-aligned) plan's frame as the back edge
  // (no further rotation), so the compass bearing carries over unchanged.
  return {
    room: {
      ...room,
      freeElements: [...shape.freeElements, ...convertObjects(raw.objects, toRoom, opts, report)],
      magneticNorthAngle: raw.magneticNorthAngle,
      magneticNorthSpreadDeg: raw.magneticNorthSpreadDeg,
    },
    report,
  };
}
